import { useState, useEffect } from "react";
import { ModalSelectHotel } from "../ModalSelectHotel";
import InputMask from "react-input-mask";
import api from "../../services/api";
import { parseIsCepRequired, normalizeHotelCep, buildHotelAddressFromLocation } from "../../functions/util";
import "./index.css";

interface HotelOption {
  pluginspaceId: number;
  name: string;
  subdomain: string;
  paymentIntegrated?: boolean;
  isCepRequired?: boolean;
  hotelCep?: string;
  hotelAddress?: {
    cep?: string;
    street?: string;
    number?: string;
    district?: string;
    city?: string;
    state?: string;
    extra?: string;
  };
}

interface HotelOrderFlowProps {
  pluginspaceId: number;
  getToken: () => Promise<string>;
  onComplete: () => void;
}

export function HotelOrderFlow({
  pluginspaceId,
  getToken,
  onComplete,
}: HotelOrderFlowProps) {
  const [hotels, setHotels] = useState<HotelOption[]>([]);
  const [selectedHotel, setSelectedHotel] = useState<HotelOption | null>(null);
  const [showHotelModal, setShowHotelModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loading, setLoading] = useState(true);

  // Login states
  const [cpf, setCpf] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");

  useEffect(() => {
    checkAndFetchHotels();
  }, []);

  const checkAndFetchHotels = async () => {
    // Verificar se já tem contexto
    const context = sessionStorage.getItem("hotelReservationContext");
    if (context) {
      const data = JSON.parse(context);

      // Se tem reservationId (já fez login), está completo
      if (data.reservationId) {
        onComplete();
        setLoading(false);
        return;
      }

      // Se hotel já foi selecionado E é viewOnly (sem integração), está completo
      if (data.hotelSelectedByUser && data.viewOnly === true) {
        onComplete();
        setLoading(false);
        return;
      }

      // Se hotel já foi selecionado MAS tem integração (viewOnly: false), precisa de login
      if (
        data.hotelSelectedByUser &&
        data.viewOnly === false &&
        !data.reservationId
      ) {
        // Buscar dados do hotel para mostrar modal de login
        try {
          const token = await getToken();
          const response = await api.post(
            `/api/store-relations/parents`,
            { child: pluginspaceId, hasLink: true },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response?.data?.data) {
            const hotelsList: HotelOption[] = response.data.data.map(
              (item: any) => ({
                pluginspaceId: item.parentPluginspace.id,
                name: item.parentPluginspace.name,
                subdomain: item.parentPluginspace.subdomain,
                paymentIntegrated: item.storesHotel?.paymentIntegrated || false,
                isCepRequired: parseIsCepRequired(item.storesHotel?.isCepRequired),
                hotelCep: normalizeHotelCep(
                  item.parentPluginspace?.address?.cep
                ),
                hotelAddress: buildHotelAddressFromLocation(
                  item.parentPluginspace?.address
                ),
              })
            );

            setHotels(hotelsList);

            // Encontrar o hotel selecionado
            const selectedHotelData = hotelsList.find(
              (h) => h.pluginspaceId === parseInt(data.pluginspaceId)
            );

            if (selectedHotelData) {
              setSelectedHotel(selectedHotelData);
              setShowLoginModal(true);
              setLoading(false);
              return;
            }
          }
        } catch (err) {
          console.error("Erro ao buscar dados do hotel para login:", err);
        }
      }
    }

    // Buscar hotéis
    try {
      const token = await getToken();
      const response = await api.post(
        `/api/store-relations/parents`,
        { child: pluginspaceId, hasLink: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response?.data?.data && response.data.data.length > 0) {
        const hotelsList: HotelOption[] = response.data.data.map(
          (item: any) => ({
            pluginspaceId: item.parentPluginspace.id,
            name: item.parentPluginspace.name,
            subdomain: item.parentPluginspace.subdomain,
            paymentIntegrated: item.storesHotel?.paymentIntegrated || false,
            isCepRequired: parseIsCepRequired(item.storesHotel?.isCepRequired),
            hotelCep: normalizeHotelCep(item.parentPluginspace?.address?.cep),
            hotelAddress: buildHotelAddressFromLocation(
              item.parentPluginspace?.address
            ),
          })
        );

        // Se todos os hotéis têm integração desativada (viewOnly), não precisa de modal
        const hasActiveIntegration = hotelsList.some(
          (h) => h.paymentIntegrated === true
        );

        if (!hasActiveIntegration) {
          // Sem integração ativa - mantém webapp normal, sem contexto de hotel
          onComplete();
          setLoading(false);
          return;
        }

        setHotels(hotelsList);

        // Se tiver apenas 1 hotel, selecionar automaticamente
        if (hotelsList.length === 1) {
          handleHotelSelect(hotelsList[0]);
        } else {
          // Se tiver mais de 1, mostrar modal de seleção
          setShowHotelModal(true);
        }
      } else {
        // Sem hotéis integrados
        onComplete();
      }
    } catch (err) {
      console.error("Erro ao buscar hotéis:", err);
      onComplete(); // Continuar mesmo com erro
    } finally {
      setLoading(false);
    }
  };

  const handleHotelSelect = (hotel: HotelOption) => {
    setSelectedHotel(hotel);
    setShowHotelModal(false);

    // Salvar contexto básico
    const hotelContext = {
      pluginspaceId: hotel.pluginspaceId,
      subdomain: hotel.subdomain,
      hotelSelectedByUser: true,
      viewOnly: !hotel.paymentIntegrated,
      isCepRequired: hotel.isCepRequired !== false,
      hotelCep: hotel.hotelCep,
      hotelAddress: hotel.hotelAddress,
    };

    sessionStorage.setItem(
      "hotelReservationContext",
      JSON.stringify(hotelContext)
    );

    // Se não precisar de login (viewOnly), finalizar
    if (!hotel.paymentIntegrated) {
      onComplete();
    } else {
      // Se precisar de login, mostrar modal de login
      setShowLoginModal(true);
    }
  };

  const handleLogin = async () => {
    if (!cpf || cpf.replace(/\D/g, "").length !== 11) {
      setLoginError("Por favor, insira um CPF válido");
      return;
    }

    if (!password) {
      setLoginError("Por favor, insira a senha");
      return;
    }

    setLoginLoading(true);
    setLoginError("");

    try {
      const token = await getToken();
      const cpfClean = cpf.replace(/\D/g, "");

      const response = await api.post(
        "/api/reservation-guest/public-login",
        {
          document: cpfClean,
          password: password,
          pluginspaceId: selectedHotel?.pluginspaceId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.data?.reservations?.length > 0) {
        const apiData = response.data.data;
        const reservation = apiData.reservations[0];

        // Atualizar contexto com dados da reserva
        const hotelContextData = {
          reservationId: reservation.id,
          doc: apiData.customer?.doc || cpfClean,
          pluginspaceId: selectedHotel?.pluginspaceId,
          subdomain: selectedHotel?.subdomain,
          roomId: reservation.room?.roomNumber || null,
          hotelSelectedByUser: true,
          viewOnly: false,
          isCepRequired: selectedHotel?.isCepRequired !== false,
          hotelCep: selectedHotel?.hotelCep,
          hotelAddress: selectedHotel?.hotelAddress,
          guestName: apiData.customer?.name,
          guestPhone: apiData.customer?.phone,
          roomNumber: reservation.room?.roomNumber
            ? String(reservation.room.roomNumber)
            : undefined,
        };

        sessionStorage.setItem(
          "hotelReservationContext",
          JSON.stringify(hotelContextData)
        );

        setShowLoginModal(false);
        onComplete();
      } else {
        setLoginError("Nenhuma reserva encontrada para este CPF");
      }
    } catch (err: any) {
      console.error("Erro ao fazer login:", err);
      setLoginError(
        err?.response?.data?.message ||
          "Erro ao validar reserva. Tente novamente."
      );
    } finally {
      setLoginLoading(false);
    }
  };

  const handleChangeHotel = () => {
    // Limpar contexto e reabrir modal de seleção
    sessionStorage.removeItem("hotelReservationContext");
    setSelectedHotel(null);
    setCpf("");
    setPassword("");
    setLoginError("");
    setShowLoginModal(false);
    setShowHotelModal(true);
  };

  if (loading) {
    return null; // Ou um loading spinner
  }

  return (
    <>
      {/* Modal de seleção de hotel */}
      <ModalSelectHotel
        isOpen={showHotelModal}
        hotels={hotels}
        onSelect={handleHotelSelect}
        onClose={() => {}} // Não permite fechar
      />

      {/* Modal de login */}
      {showLoginModal && selectedHotel && (
        <div className="modal-hotel-login-overlay">
          <div className="modal-hotel-login">
            <div className="modal-hotel-login-header">
              <h2>Login da Reserva</h2>
              <p>
                Hotel: <strong>{selectedHotel.name}</strong>
              </p>
              {hotels.length > 1 && (
                <button
                  onClick={handleChangeHotel}
                  className="btn-change-hotel-link"
                >
                  Trocar hotel
                </button>
              )}
            </div>

            <div className="modal-hotel-login-body">
              <div className="hotel-login-input-group">
                <label htmlFor="cpf-login">CPF do Hóspede</label>
                <InputMask
                  id="cpf-login"
                  mask="999.999.999-99"
                  value={cpf}
                  onChange={(e) => {
                    setCpf(e.target.value);
                    setLoginError("");
                  }}
                  placeholder="000.000.000-00"
                  disabled={loginLoading}
                  className="hotel-login-input"
                />
              </div>

              <div className="hotel-login-input-group">
                <label htmlFor="password-login">Senha da Reserva</label>
                <input
                  id="password-login"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setLoginError("");
                  }}
                  placeholder="Digite a senha"
                  disabled={loginLoading}
                  className="hotel-login-input"
                />
              </div>

              {loginError && <p className="hotel-login-error">{loginError}</p>}

              <button
                onClick={handleLogin}
                disabled={loginLoading || !cpf || !password}
                className="hotel-login-btn"
              >
                {loginLoading ? "Validando..." : "Entrar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
